<template>
  <div>


    <!-- VehicleFilters now manages the layout including a potential sidebar and the main content area via slot -->
    <VehicleFilters
      ref="vehicleFiltersRef"
      @filter-change="handleFilterChange"
      @filter-clear="handleFilterClear"
      @clear-all-filters="handleClearAllFilters"
    >
      <!-- This is the main content area that will be slotted into VehicleFilters -->
      <div class="sm:px-2 sm:py-4 w-full lg:flex-1 bg-background text-foreground"> 
        <div class="flex flex-col sm:flex hidden md:flex-row justify-between items-center mb-5 bg-background">
          <!-- Left Grouping Div -->
          <div class="flex flex-col lg:flex-row items-center gap-x-6 gap-y-4 mb-4 md:mb-0">
            
            <!-- Real-time Status Indicator -->
            <div class="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
              <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>Live Market</span>
            </div>
            <!-- Sort by -->
            <div class="flex items-center gap-4">
              <span class="text-sm font-medium text-muted-foreground">Sort by:</span>
              <Select
                v-model="sortOptionForSelect"
                class="w-48 backdrop-blur-xs bg-white/50 dark:bg-gray-900/50 border-white/20 dark:border-gray-800/20"
              >
                <SelectTrigger
                  class="flex h-10 w-48 items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <SelectValue placeholder="Sort order" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="relevance">
                    <div class="flex items-center gap-2">
                      <ArrowPathIcon class="h-4 w-4" />
                      <span>Reset to Default</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="relevance">Most Relevant</SelectItem>
                  <SelectItem value="price_asc">Price: Low to High</SelectItem>
                  <SelectItem value="price_desc">Price: High to Low</SelectItem>
                  <SelectItem value="year_desc">Newest First</SelectItem>
                  <SelectItem value="year_asc">Oldest First</SelectItem>
                   <SelectItem value="updated_desc">Recently Updated</SelectItem>
                   <SelectItem value="kms_asc">Mileage: Low to High</SelectItem>
                   <SelectItem value="kms_desc">Mileage: High to Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <!-- View -->
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-muted-foreground">View:</span>
              <div class="flex items-center gap-1 bg-white/50 dark:bg-gray-900/50 rounded-lg p-1 backdrop-blur-xs border border-white/20 dark:border-gray-800/20">
                <button
                  @click="handleViewModeChange('grid')"
                  class="p-2 rounded-md transition-all duration-200"
                  :class="cardLayout === 'grid' ? 'bg-primary text-primary-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground hover:bg-white/50 dark:hover:bg-gray-800/50'"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  @click="handleViewModeChange('list')"
                  class="p-2 rounded-md transition-all duration-200"
                  :class="cardLayout === 'list' ? 'bg-primary text-primary-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground hover:bg-white/50 dark:hover:bg-gray-800/50'"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <button
                  @click="handleViewModeChange('advanced-list')"
                  class="p-2 rounded-md transition-all duration-200"
                  :class="cardLayout === 'advanced-list' ? 'bg-primary text-primary-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground hover:bg-white/50 dark:hover:bg-gray-800/50'"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Right Grouping Div -->
          <div class="flex flex-col md:flex-row items-center gap-x-6 gap-y-4">
            <!-- Card Style -->
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-muted-foreground">Card Style:</span>
              <Select
                v-model="selectedCardType"
                class="w-48 backdrop-blur-xs bg-white/50 dark:bg-gray-900/50 border-white/20 dark:border-gray-800/20"
              >
                <SelectTrigger
                  class="flex h-10 w-48 items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <SelectValue placeholder="Card Style" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ModernVehicleCard">Modern</SelectItem>
                  <SelectItem value="ClassicVehicleCard">Classic</SelectItem>
                  <SelectItem value="RentalVehicleCard">Rental</SelectItem>
                  <SelectItem value="CompactVehicleCard">Compact</SelectItem>
                  <SelectItem value="PromoVehicleCard">Promo</SelectItem>
                  <SelectItem value="MinimalVehicleCard">Minimal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <!-- NEW: Display Mode Toggle -->
            <div class="flex items-center gap-2">
              <div class="flex items-center gap-1 bg-white/50 dark:bg-gray-900/50 rounded-lg p-1 backdrop-blur-xs border border-white/20 dark:border-gray-800/20">
                <button
                  @click="displayMode = 'infinite'"
                  class="p-2 rounded-md transition-all duration-200 text-xs"
                  :class="displayMode === 'infinite' ? 'bg-primary text-primary-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground hover:bg-white/50 dark:hover:bg-gray-800/50'"
                >
                  Infinite
                </button>
                <button
                  @click="displayMode = 'paginated'"
                  class="p-2 rounded-md transition-all duration-200 text-xs"
                  :class="displayMode === 'paginated' ? 'bg-primary text-primary-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground hover:bg-white/50 dark:hover:bg-gray-800/50'"
                >
                  Paginated
                </button>
              </div>
            </div>
            

            <!-- UPDATED SEARCH INPUT FIELD with AI keyword suggestions -->
            <div class="relative w-64"> 
              <Input
                v-model="searchKeywords"
                @input="handleSearchInputWithSuggestions"
                @focus="showSuggestions = true"
                @blur="handleSearchBlur"
                @keyup.enter="handleSearchInput"
                type="text"
                placeholder="Search by keyword..."
                class="w-full h-10 pl-10 pr-10 py-2 text-sm rounded-md border border-input bg-background ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
              <span class="absolute start-0 inset-y-0 flex items-center justify-center px-2">
                <Search class="size-6 text-muted-foreground" />
              </span>
              <button
                v-if="searchKeywords && searchKeywords.length > 0"
                @click="clearSearchWithSuggestions"
                type="button"
                class="absolute right-3 top-1/2 -translate-y-1/2 transform text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-5 w-5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
              
              <!-- Keyword Suggestions Dropdown -->
              <div 
                v-if="showSuggestions && keywordSuggestions.length > 0" 
                class="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto"
              >
                <div class="p-2">
                  <div class="text-xs text-gray-500 dark:text-gray-400 mb-2 px-2">
                    Suggestions
                  </div>
                  <div
                    v-for="(suggestion, index) in keywordSuggestions"
                    :key="`${suggestion.text}-${index}`"
                    @mousedown="selectSuggestion(suggestion.text, suggestion)"
                    class="flex items-center justify-between px-3 py-2 rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div class="flex items-center gap-2">
                      <span 
                        class="text-xs px-2 py-1 rounded-full text-white font-medium"
                        :class="getSuggestionCategoryColor(suggestion.category)"
                      >
                        {{ getSuggestionCategoryLabel(suggestion.category) }}
                      </span>
                      <span class="text-sm">{{ suggestion.text }}</span>
                    </div>
                    <span 
                      v-if="suggestion.matchCount" 
                      class="text-xs text-gray-500 dark:text-gray-400"
                    >
                      {{ suggestion.matchCount }} cars
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mb-6 bg-background">
          <h1 class="text-3xl font-bold mb-2 text-foreground">{{ filteredVehiclesCount }} Cars For Sale</h1>
          <p class="text-muted-foreground">Browse our selection of quality vehicles</p>
        </div>

        
        <!-- Vehicle cards -->
        <div v-if="cardLayout !== 'advanced-list' && ((displayMode === 'infinite' && items.length > 0) || (displayMode === 'paginated' && paginatedItems.length > 0))"
          :class="{
            'grid grid-flow-row auto-rows-fr gap-6 w-full grid-cols-1 md:grid-cols-[repeat(auto-fill,minmax(400px,1fr))]': cardLayout === 'grid',
            'flex flex-col gap-6': cardLayout === 'list'
          }"
        >
          <component
            :is="currentCardComponent"
            v-for="vehicle in (displayMode === 'infinite' ? items : paginatedItems)"
            :key="vehicle.id"
            :vehicle="vehicle"
            :layout="cardLayout"
            class="h-full"
            @quick-view="openQuickView"
            @favorite="vehiclesStore.toggleFavorite"
          />
        </div>
        
        <!-- Advanced List View with toggle -->
        <div v-else-if="cardLayout === 'advanced-list' && ((displayMode === 'infinite' && items.length > 0) || (displayMode === 'paginated' && paginatedItems.length > 0))">
          <VehicleCardWithToggle 
            :vehicles="displayMode === 'infinite' ? items : paginatedItems"
            :default-style="'airbnb'"
          />
        </div>

        <!-- Empty state -->
        <div v-else class="text-center py-12 bg-background">
          <h3 class="text-xl font-medium mb-2 text-foreground">No vehicles found</h3>
          <p class="text-muted-foreground">Try adjusting your filters or check back later for new inventory.</p>
        </div>

        <!-- PAGINATION CONTROLS -->
        <div v-if="displayMode === 'paginated' && totalPagesForPagination > 0" class="flex justify-center items-center gap-4 mt-8 mb-4 bg-background">
          <button @click="prevPage" :disabled="currentPageForPagination === 1" class="px-4 py-2 border border-border bg-background text-foreground rounded disabled:opacity-50 hover:bg-accent transition-colors">Previous</button>
          <span class="text-foreground">Page {{ currentPageForPagination }} of {{ totalPagesForPagination }}</span>
          <button @click="nextPage" :disabled="currentPageForPagination === totalPagesForPagination" class="px-4 py-2 border border-border bg-background text-foreground rounded disabled:opacity-50 hover:bg-accent transition-colors">Next</button>
        </div>
      </div>
    </VehicleFilters>

    <!-- Mobile Filters Sheet (This is now handled by VehicleFilters.vue, so related variables might be unused) -->
    <!--
    <Sheet v-model:open="isFiltersVisible" class="lg:hidden">
      <SheetContent side="left" class="w-full sm:max-w-md p-0">
        <div class="h-full flex flex-col">
          <div class="flex items-center justify-between p-6 border-b border-border">
            <h2 class="text-xl font-semibold bg-linear-to-r from-primary to-primary/60 bg-clip-text text-transparent">Filters</h2>
            <button
              v-if="actualActiveFilterCount > 0"
              @click="handleClearAllFilters"
              class="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Clear all
            </button>
          </div>
          <div class="flex-1 overflow-y-auto">
            <div class="p-6 space-y-6">
              <FilterPresets class="py-2 border-b border-border" />
              <SavedFilters class="py-2 border-b border-border" />
              <SuggestedFilters class="py-2" />
              <ActiveFilters
                :active-filters="vehiclesStore.activeFilters"
                :active-filter-count="actualActiveFilterCount"
                @clear-filter="handleFilterClear"
                @clear-all-filters="handleClearAllFilters"
                class="px-4 py-2 border-b border-border"
              />
            </div>
          </div>
          <div class="p-6 border-t border-border">
            <button
              @click="isFiltersVisible = false"
              class="w-full px-4 py-3 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-all duration-200 shadow-xs"
            >
              Show {{ filteredVehiclesCount }} cars
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
    -->

    <!-- VehicleQuickView Component -->
    <VehicleQuickView
      :vehicle-id="vehicleIdForQuickViewProp"
      :is-open="isQuickViewOpen"
      @close="handleQuickViewClose"
      :filtered-vehicle-ids="filteredVehicleIds"
      @navigate="handleNavigate"
    />

    <!-- Loading Overlay for Infinite Scroll Restore -->
    <div v-if="isRestoringInfiniteScroll" 
         class="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[9999]">
      <div class="text-foreground text-xl p-4 bg-background border border-border rounded-lg shadow-xl">
        Restoring view...
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick, onBeforeUnmount } from 'vue'
import { useVehiclesStore } from '~/stores/vehicles'
// import { useVehicleQuickView } from '~/composables/useVehicleQuickView'
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '~/layers/base/components/ui/select'
import { ArrowPathIcon } from '@heroicons/vue/24/outline'
import ModernVehicleCard from '~/layers/base/components/vehicle-cards/ModernVehicleCard.vue'
import VehicleQuickView from '~/layers/base/components/VehicleQuickView.vue'
import VehicleFilters from '~/layers/base/components/VehicleFilters.vue'
import { useRoute, useRouter } from '#imports'
import type { Vehicle } from '~/types'
import { useInfiniteScrollCustom } from '~/layers/base/composables/useInfiniteScroll'
// import { usePendingScrollStore } from '~/composables/usePendingScrollStore'
import { useDebounceFn } from '@vueuse/core'
import { Search } from 'lucide-vue-next'
import { Input } from '~/layers/base/components/ui/input'
// import { Sheet, SheetContent } from '~/layers/base/components/ui/sheet'
// import FilterPresets from '~/layers/base/components/filters/FilterPresets.vue'
// import SavedFilters from '~/layers/base/components/filters/SavedFilters.vue'
// import SuggestedFilters from '~/layers/base/components/filters/SuggestedFilters.vue'
// import ActiveFilters from '~/layers/base/components/filters/ActiveFilters.vue'

// Import other card components
import ClassicVehicleCard from '~/layers/base/components/vehicle-cards/ClassicVehicleCard.vue'
import RentalVehicleCard from '~/layers/base/components/vehicle-cards/RentalVehicleCard.vue'
import CompactVehicleCard from '~/layers/base/components/vehicle-cards/CompactVehicleCard.vue'
import PromoVehicleCard from '~/layers/base/components/vehicle-cards/PromoVehicleCard.vue'
import MinimalVehicleCard from '~/layers/base/components/vehicle-cards/MinimalVehicleCard.vue'
// // import { Input } from '~/layers/base/components/ui/input'

// Import the VehicleCardWithToggle component
import VehicleCardWithToggle from '~/layers/base/components/vehicle-cards/VehicleCardWithToggle.vue'
import AirbnbStyleVehicleCardList from '~/layers/base/components/vehicle-cards/AirbnbStyleVehicleCardList.vue'
import ListStyleToggle from '~/layers/base/components/vehicle-cards/ListStyleToggle.vue'

// Import keyword suggestions composable
const { 
  suggestions: keywordSuggestions, 
  loading: suggestionsLoading, 
  getSuggestions, 
  clearSuggestions 
} = useKeywordSuggestions()

const vehiclesStore = useVehiclesStore()

// Keyword suggestions state
const showSuggestions = ref(false)

// Card layout state
// NEW: Display mode state (infinite scroll vs. pagination)
const displayMode = ref<'infinite' | 'paginated'>('infinite');
const cardLayout = ref('grid') // Default to 'grid'
const selectedCardType = ref<'ModernVehicleCard' | 'ClassicVehicleCard' | 'RentalVehicleCard' | 'CompactVehicleCard' | 'PromoVehicleCard' | 'MinimalVehicleCard'>('ModernVehicleCard')

// Define the card components mapping
const cardComponents = {
  ModernVehicleCard,
  ClassicVehicleCard,
  RentalVehicleCard,
  CompactVehicleCard,
  PromoVehicleCard,
  MinimalVehicleCard
}

const currentCardComponent = computed(() => {
  return cardComponents[selectedCardType.value] || ModernVehicleCard // Fallback to ModernVehicleCard
})

// Local state for UI bindings (might still be useful or can be replaced by computed)
const activeCondition = ref('all')
const searchKeywords = ref('')

// Handle search input - Reset all filters when searching
const debouncedApplySearch = useDebounceFn(() => {
  // console.log('[CarsForSale] Debounced search applying with:', searchKeywords.value);
  // Reset all filters and only apply search keywords
  vehiclesStore.applyFilters({
    search_keywords: searchKeywords.value.trim() || undefined,
  });
}, 500);

const handleSearchInput = () => {
  // This function is called on @input from the search field
  // It resets all filters and applies only the search keywords
  debouncedApplySearch();
};

const clearSearch = () => {
  searchKeywords.value = '';
  handleSearchInput(); // Trigger filter update
};

// Keyword suggestions functions
const handleSearchInputWithSuggestions = (event: any) => {
  const value = event.target?.value || event
  searchKeywords.value = value
  
  // Get suggestions if there's text
  if (value && value.trim().length > 0) {
    getSuggestions(value.trim())
    showSuggestions.value = true
  } else {
    clearSuggestions()
    showSuggestions.value = false
  }
  
  // Also trigger the regular debounced search
  debouncedApplySearch()
}

const handleSearchBlur = () => {
  // Delay hiding suggestions to allow for clicks
  setTimeout(() => {
    showSuggestions.value = false
  }, 200)
}

const clearSearchWithSuggestions = () => {
  searchKeywords.value = ''
  clearSuggestions()
  showSuggestions.value = false
  // Clear all filters when search is cleared
  vehiclesStore.applyFilters({})
}

const selectSuggestion = (suggestionText: string, suggestion?: any) => {
  // Find the full suggestion object to get the category
  const fullSuggestion = keywordSuggestions.value.find(s => s.text === suggestionText)
  const category = suggestion?.category || fullSuggestion?.category
  
  // Apply the correct filter type based on the suggestion category
  const newFilters = { ...vehiclesStore.activeFilters }
  
  if (category === 'color') {
    // Apply as a colour filter
    newFilters.colour = [suggestionText]
    // Clear search keywords since this is a specific filter
    delete newFilters.search_keywords
    searchKeywords.value = ''
  } else if (category === 'make') {
    // Apply as a make filter
    newFilters.make = [suggestionText]
    delete newFilters.search_keywords
    searchKeywords.value = ''
  } else if (category === 'model') {
    // Handle model suggestions that might include make name
    const modelSuggestion = suggestionText.trim()
    
    // Check if this looks like "Make Model" format (two words, first capitalized)
    const words = modelSuggestion.split(' ')
    if (words.length >= 2) {
      // This might be a "Make Model" combination like "Kia Koleos"
      const possibleMake = words[0]
      const possibleModel = words.slice(1).join(' ')
      
      // Check if the first word matches any available make
      const availableMakes = (vehiclesStore as any).availableFilters?.makes || []
            const matchingMake = availableMakes.find((make: any) =>
        make.displayValue?.toLowerCase() === (possibleMake || '').toLowerCase() ||
        make.value?.toLowerCase() === (possibleMake || '').toLowerCase()
      )
      
      if (matchingMake) {
        // Set both make and model filters
        newFilters.make = [matchingMake.value || matchingMake.displayValue]
        
        // For model, we need to find the correct model value in make_model format
        const availableModels = (vehiclesStore as any).availableFilters?.models || []
        const makeValue = (matchingMake.value || matchingMake.displayValue).toLowerCase()
        const modelValue = possibleModel.toLowerCase().replace(/\s+/g, '-')
        const expectedModelValue = `${makeValue}_${modelValue}`
        
        // Find matching model by either the expected format or display value
        const matchingModel = availableModels.find((model: any) => 
          model.value?.toLowerCase() === expectedModelValue ||
          model.displayValue?.toLowerCase() === possibleModel.toLowerCase()
        )
        
        if (matchingModel) {
          newFilters.model = [matchingModel.value || matchingModel.displayValue]
        } else {
          // Fallback: use the expected format
          newFilters.model = [expectedModelValue]
        }
      } else {
        // No matching make found, treat as regular model search
        newFilters.model = [modelSuggestion]
      }
    } else {
      // Single word model, apply as-is
      newFilters.model = [modelSuggestion]
    }
    
    delete newFilters.search_keywords
    searchKeywords.value = ''
  } else if (category === 'bodyType') {
    // Apply as a body filter
    newFilters.body = [suggestionText]
    delete newFilters.search_keywords
    searchKeywords.value = ''
  } else if (category === 'fuelType') {
    // Apply as a fuel filter
    newFilters.fuel = [suggestionText]
    delete newFilters.search_keywords
    searchKeywords.value = ''
  } else {
    // For features, synonyms, or unknown categories, use search_keywords
    newFilters.search_keywords = suggestionText
    searchKeywords.value = suggestionText
  }
  
  vehiclesStore.applyFilters(newFilters)
  showSuggestions.value = false
  clearSuggestions()
}

const getSuggestionCategoryColor = (category: string) => {
  const colors = {
    make: 'bg-blue-500',
    model: 'bg-green-500', 
    feature: 'bg-purple-500',
    color: 'bg-pink-500',
    bodyType: 'bg-orange-500',
    fuelType: 'bg-yellow-500',
    synonym: 'bg-gray-500'
  }
  return colors[category as keyof typeof colors] || 'bg-gray-500'
}

const getSuggestionCategoryLabel = (category: string) => {
  const labels = {
    make: 'Make',
    model: 'Model',
    feature: 'Feature', 
    color: 'Color',
    bodyType: 'Type',
    fuelType: 'Fuel',
    synonym: 'Related'
  }
  return labels[category as keyof typeof labels] || category
}

// Set condition filter
const setCondition = (condition: string) => {
  activeCondition.value = condition // Keep local ref potentially for immediate UI feedback
  vehiclesStore.applyFilters({ 
    ...vehiclesStore.activeFilters, 
    condition: condition === 'all' ? undefined : [condition] // Use applyFilters, set undefined to remove
  })
}

// Watch for layout changes and save to localStorage
watch(cardLayout, (newLayout) => {
  if (process.client) localStorage.setItem('preferredViewMode', newLayout)
})

watch(selectedCardType, (newCardType) => {
  if (process.client) localStorage.setItem('preferredCardType', newCardType)
})

// Handle view mode change
const handleViewModeChange = (mode: 'grid' | 'list' | 'advanced-list') => {
  cardLayout.value = mode
  // If switching to advanced-list, we want to make sure the display mode is set to 'infinite'
  if (mode === 'advanced-list' && displayMode.value === 'paginated') {
    displayMode.value = 'infinite'
  }
}

// Filters state
const vehicleFiltersRef = ref()

// Quick view state
const { quickViewVehicleId, isQuickViewOpen, openQuickView, closeQuickView } = useVehicleQuickView()

const route = useRoute()
const router = useRouter()
const isMounted = ref(false)
const scrollContainer = ref<HTMLElement | null>(null) // For window scroll, this might be less critical or null
const isRestoringInfiniteScroll = ref(false); // NEW: Loading state for infinite scroll restoration

// --- Infinite Scroll Setup ---
// Options for useInfiniteScroll. enableUrlSync is true, so it will try to manage URL page.
// We will need to coordinate this with pagination mode.
const { 
  items, 
  loading: infiniteScrollLoading,
  allItemsLoaded, 
  loadMore, // We will guard calls to this based on displayMode
  error: infiniteScrollError,
  totalItems, 
  currentPage, // This is useInfiniteScroll's current page, synced with URL if enableUrlSync is true
  reset: resetInfiniteScroll 
} = useInfiniteScrollCustom({
  itemsPerPage: 24,
  scrollTarget: process.client ? window : null, // Standard scroll target
  distance: 3000, // INCREASED further: Load when 3000px from bottom
  dataSource: vehiclesStore,
  resetOnDataChange: true, // Important for filters/sort changes
  immediate: true, // Load first page on mount by default
  debug: true,
  identifier: 'carsForSalePageFullScroll',
  storageKey: 'carsForSaleScrollState_full', 
  enableUrlSync: true, // Let composable handle ?page= in URL by default
  cacheItems: true, 
  enableCacheTrimming: true,
  cacheTrimThreshold: 200,
  retainWindowSize: 3,
  debounceWait: 50,
  // 'enabled' is not a standard option, so removed. We manage behavior externally.
});

// --- Pagination State (for displayMode === 'paginated') ---
const currentPageForPagination = ref(1);
const itemsPerPageForPagination = 24; // Constant, matches infinite scroll

const paginatedItems = computed(() => {
  if (displayMode.value === 'paginated') {
    const start = (currentPageForPagination.value - 1) * itemsPerPageForPagination;
    const end = start + itemsPerPageForPagination;
    return vehiclesStore.filteredVehicles.slice(start, end);
  }
  return [];
});

const totalPagesForPagination = computed(() => {
  if (displayMode.value === 'paginated' && vehiclesStore.filteredVehicles.length > 0) {
    return Math.ceil(vehiclesStore.filteredVehicles.length / itemsPerPageForPagination);
  }
  return 1;
});

// --- Methods for Pagination ---
const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPagesForPagination.value) {
    currentPageForPagination.value = page;
    router.replace({ query: { ...route.query, page: String(page) } });
    scrollToTop();
  }
};

const prevPage = () => {
  goToPage(currentPageForPagination.value - 1);
};

const nextPage = () => {
  goToPage(currentPageForPagination.value + 1);
};

// Computed property for all vehicle IDs (used by quick view)
const allVehicleIdsForQuickView = computed(() => vehiclesStore.filteredVehicles.map((v: Vehicle) => v.id))

// Local ref for the select v-model, synced with store's sortOption
const sortOptionForSelect = ref(vehiclesStore.sortOption)

// NEW Watcher for sortOptionForSelect (when UI changes dropdown)
watch(sortOptionForSelect, (newSelectedSortValue, oldSelectedSortValue) => {
  if (newSelectedSortValue !== oldSelectedSortValue) { // Check if the value actually changed
    // Update the store if its current sortOption is different
    if (vehiclesStore.sortOption !== newSelectedSortValue) {
      vehiclesStore.setSortOption(newSelectedSortValue);
    }

    // Update the URL query parameter if it's different
    // This preserves other query parameters
    if (route.query.sort !== newSelectedSortValue) {
      router.replace({ query: { ...route.query, sort: newSelectedSortValue } });
    }
  }
});

// Existing watcher for route.query.sort (when URL changes) - Ensure it is robust
watch(() => route.query.sort, (newUrlSortParam) => {
  const currentUiSort = sortOptionForSelect.value;
  const currentStoreSort = vehiclesStore.sortOption;
  const defaultSortOption = 'relevance'; // CHANGED from 'price_asc'

  let targetSort = defaultSortOption;
  // Check if newUrlSortParam is a string and not empty
  if (newUrlSortParam && typeof newUrlSortParam === 'string') {
    // Ensure the sort option from URL is one of the valid options
    const validSortOptions = ['relevance', 'price_asc', 'price_desc', 'year_desc', 'year_asc', 'updated_desc', 'kms_asc', 'kms_desc'];
    if (validSortOptions.includes(newUrlSortParam)) {
        targetSort = newUrlSortParam;
    } // else it stays as defaultSortOption ('relevance')
  } else if (newUrlSortParam === null || newUrlSortParam === undefined) {
    // If sort param is explicitly removed or not present, revert to default
    targetSort = defaultSortOption;
  }

  // Update the v-model ref for the dropdown if it's not matching the targetSort
  if (currentUiSort !== targetSort) {
    sortOptionForSelect.value = targetSort;
  }

  // Update the store if it's not matching the targetSort
  if (currentStoreSort !== targetSort) {
    vehiclesStore.setSortOption(targetSort);
  }
}, { immediate: true });

// Existing watcher to sync sortOptionForSelect if store changes from elsewhere
watch(() => vehiclesStore.sortOption, (newStoreSortOption) => {
  if (sortOptionForSelect.value !== newStoreSortOption) {
    sortOptionForSelect.value = newStoreSortOption;
  }
});

// NEW HELPER FUNCTION to extract filter parts for URL query
function getFilterQueryParts(filters: Record<string, any>): Record<string, string | undefined> {
  const queryForRouter: Record<string, string | undefined> = {};
  for (const key in filters) {
    const value = filters[key];
    if (value === null || value === undefined || value === '') continue;

    if (Array.isArray(value)) {
      if (value.length > 0) {
        queryForRouter[key] = value.join(',');
      }
    } else if (typeof value === 'object' && value !== null && 'min' in value && 'max' in value) {
      const rangeKey = `${key}Range` as any;
      const defaultRange = (vehiclesStore as any).availableFilters[rangeKey];
      let isDefault = false;
      if (defaultRange && typeof defaultRange === 'object' && 'min' in defaultRange && 'max' in defaultRange) {
        // @ts-ignore
        if (value.min === defaultRange.min && value.max === defaultRange.max) {
            isDefault = true;
        }
      }
      if (!isDefault) {
          queryForRouter[`${key}_min`] = String(value.min);
          queryForRouter[`${key}_max`] = String(value.max);
      }
    } else if (typeof value === 'boolean') {
        if (value === true) { // Only include if true, effectively removing if false
          queryForRouter[key] = 'true';
        }
    } else if (typeof value === 'string' && value.trim()) {
        queryForRouter[key] = value.trim();
    } else if (typeof value === 'number') {
        queryForRouter[key] = String(value);
    }
  }
  return queryForRouter;
}

// --- URL Sync Logic (Page-level for filters & sort, infiniteScroll handles page) ---
const updateUrlQuery = (filters: Record<string, any>) => {
  if (!route || !route.query) return
  const queryForRouter: Record<string, string | undefined> = {}

  // Process filters for the router query
  for (const key in filters) {
    const value = filters[key]
    if (value === null || value === undefined || value === '') continue

    if (Array.isArray(value)) {
      if (value.length > 0) {
        queryForRouter[key] = value.join(',')
      } // If empty array, effectively remove the key (by not adding it)
    } else if (typeof value === 'object' && value !== null && 'min' in value && 'max' in value) {
      // Handle range objects - Check if different from defaults
      const rangeKey = `${key}Range` as any
      const defaultRange = (vehiclesStore as any).availableFilters[rangeKey]
      let isDefault = false
      if (defaultRange && typeof defaultRange === 'object' && 'min' in defaultRange && 'max' in defaultRange) {
        if (value.min === defaultRange.min && value.max === defaultRange.max) {
            isDefault = true
        }
      }
      if (!isDefault) {
          queryForRouter[`${key}_min`] = String(value.min)
          queryForRouter[`${key}_max`] = String(value.max)
      }
    } else if (typeof value === 'boolean') {
        if (value === true) {
          queryForRouter[key] = 'true'
        } // If false, effectively remove
    } else if (typeof value === 'string' && value.trim()) { // Ensure string isn't just whitespace
        queryForRouter[key] = value.trim()
    } else if (typeof value === 'number') { // Handle plain numbers if any
        queryForRouter[key] = String(value)
    }
  }

  // Preserve sort and quickView (if present)
  if (route.query.sort) queryForRouter.sort = route.query.sort as string
  if (route.query.quickView) queryForRouter.quickView = route.query.quickView as string

  // Add Sort Option from store
  if (vehiclesStore.sortOption !== 'price-asc') { // Default for price-asc might change
    queryForRouter.sort = vehiclesStore.sortOption
  }

  // Only replace if the query actually changes
  const currentQueryNormalized = JSON.stringify(route.query || {})
  const newQueryNormalized = JSON.stringify(queryForRouter || {})
  if (newQueryNormalized !== currentQueryNormalized) {
    router.replace({ query: queryForRouter })
  }
}

watch(() => vehiclesStore.activeFilters, (newFilters) => {
  const filterQueryParts = getFilterQueryParts(newFilters || {});
  let finalQuery = { ...route.query, ...filterQueryParts };

  // Clean up: Remove any keys from finalQuery that were part of old filters but not new,
  // and are not special keys (sort, quickView, page).
  const currentFilterKeys = Object.keys(filterQueryParts);
  const specialKeys = ['sort', 'quickView', 'page'];
  for (const key in finalQuery) {
    if (!currentFilterKeys.includes(key) && !specialKeys.includes(key) && route.query.hasOwnProperty(key) && !newFilters?.hasOwnProperty(key)) {
        // Check if this key was part of the general query params and not a filter key that persists (like a default)
        // This part is tricky: ensure we only remove stale filter params.
        // A simpler approach: build query from scratch.
    }
  }
  // Rebuild query to be safer: start with special params, add new filters.
  const preservedQuery: Record<string, any> = {};
  if (route.query.sort) preservedQuery.sort = route.query.sort;
  if (route.query.quickView) preservedQuery.quickView = route.query.quickView;
  
  finalQuery = { ...preservedQuery, ...filterQueryParts };

  if (displayMode.value === 'paginated') {
    finalQuery.page = '1';
    currentPageForPagination.value = 1; // Sync state
  } else {
    // For infinite scroll, its resetOnDataChange should handle resetting its page.
    // If its enableUrlSync is true, it will set page=1 (or its initial page) in URL.
    // Explicitly removing page from finalQuery lets infinite scroll control it.
    delete finalQuery.page;
  }
  
  // Only replace if query actually changes to avoid navigation loops
  if (JSON.stringify(router.currentRoute.value.query) !== JSON.stringify(finalQuery)) {
    router.replace({ query: finalQuery });
    // After filters change and URL is updated, scroll to top.
    nextTick(() => {
      scrollToTop(); 
      // Ensure infinite scroll also visually reflects its page 1 if it reset.
      // Its internal reset due to resetOnDataChange should handle its items.
    });
  }

}, { deep: true })

watch(() => route.query, (newQuery, oldQuery) => {
  if (JSON.stringify(newQuery) === JSON.stringify(oldQuery)) return

  const extractFilterPartsOnly = (query: typeof route.query | undefined) => {
    if (!query) return {};
    const parts = { ...query };
    delete parts.page;
    delete parts.sort;
    delete parts.quickView;
    return parts;
  };

  const currentFilterQueryParts = extractFilterPartsOnly(newQuery);
  const previousFilterQueryParts = extractFilterPartsOnly(oldQuery);

  if (JSON.stringify(currentFilterQueryParts) !== JSON.stringify(previousFilterQueryParts)) {
    vehiclesStore.applyFilters(currentFilterQueryParts || {});
  }
  
  const querySort = (newQuery.sort as string) || 'relevance'; // Default from sort watcher
  if (vehiclesStore.sortOption !== querySort) {
    vehiclesStore.setSortOption(querySort);
  }

  // Handle page changes from URL (e.g., back/forward button)
  const pageFromUrl = newQuery.page ? parseInt(newQuery.page as string, 10) : 1;
  if (displayMode.value === 'paginated') {
    if (!isNaN(pageFromUrl) && pageFromUrl > 0 && currentPageForPagination.value !== pageFromUrl) {
      currentPageForPagination.value = pageFromUrl;
      // scrollToTop(); // Optional: scroll to top if page changes via URL, might be handled by goToPage if internal
    }
  }
  // If displayMode is 'infinite', useInfiniteScroll (with enableUrlSync: true) should react to page changes in URL.

}, { deep: true, immediate: true }); // immediate: true ensures it runs on load to sync store with initial URL filters.

// --- Scroll State Persistence & Restoration ---
const { consumePendingScroll } = usePendingScrollStore()
const STORAGE_KEY_SCROLL = 'carsForSaleScrollState_full' // Consistent key
const ESTIMATED_ITEM_HEIGHT_GRID = 380; // Example for grid, ADJUST THIS
const ESTIMATED_ITEM_HEIGHT_LIST = 180; // Example for list, ADJUST THIS

const estimatedItemHeight = computed(() => {
  return cardLayout.value === 'grid' ? ESTIMATED_ITEM_HEIGHT_GRID : ESTIMATED_ITEM_HEIGHT_LIST;
});

// Debounced function to save scroll state
const debouncedSaveScrollState = useDebounceFn(() => {
  if (process.client) {
    const state = {
      page: currentPage.value,
      scrollTop: window.scrollY, // y.value from useScroll might be an option too
      timestamp: Date.now(),
    }
    // console.log(`[CarsForSale] DEBOUNCED SAVING scroll state for ${route.fullPath}:`, JSON.stringify(state))
    localStorage.setItem(STORAGE_KEY_SCROLL, JSON.stringify(state))
  }
}, 200); // Debounce by 200ms

const saveScrollState = () => { // This can be the immediate save, potentially called by onBeforeUnmount
  if (process.client) {
    const state = {
      page: currentPage.value, // Current loaded page from infinite scroll
      scrollTop: window.scrollY,
      timestamp: Date.now(),
    }
    // console.log(`[CarsForSale] SAVING scroll state for ${route.fullPath}:`, JSON.stringify(state))
    localStorage.setItem(STORAGE_KEY_SCROLL, JSON.stringify(state))
  }
}

const loadAndRestoreScroll = async () => {
  // console.log('[CFS Pagi Debug] loadAndRestoreScroll: START', { mode: displayMode.value });

  const scrollTargetToRestore = consumePendingScroll(route.fullPath); 
  // console.log('[CFS Pagi Debug] loadAndRestoreScroll: scrollTargetToRestore (from router):', scrollTargetToRestore ? JSON.parse(JSON.stringify(scrollTargetToRestore)) : null);
  
  let storedStateForPage = null;
  let storedStateRaw = null;

  if (process.client && displayMode.value === 'infinite') { 
      storedStateRaw = localStorage.getItem(STORAGE_KEY_SCROLL);
      if (storedStateRaw) {
          try { 
            storedStateForPage = JSON.parse(storedStateRaw);
          } catch (e) { 
            console.error('[CarsForSale] Bad scroll state in localStorage:', e);
            localStorage.removeItem(STORAGE_KEY_SCROLL); 
          }
      }
  }
  // console.log('[CFS Pagi Debug] loadAndRestoreScroll: storedStateForPage (from localStorage, for infinite mode):', storedStateForPage ? JSON.parse(JSON.stringify(storedStateForPage)) : null);

  const positionToRestore = 
    (scrollTargetToRestore && scrollTargetToRestore.top !== undefined && scrollTargetToRestore.left !== undefined) ? scrollTargetToRestore : 
    (displayMode.value === 'infinite' && storedStateForPage && storedStateForPage.scrollTop !== undefined) ? { top: storedStateForPage.scrollTop, left: 0, pageFromStorage: storedStateForPage.page } : 
    null;
  // console.log('[CFS Pagi Debug] loadAndRestoreScroll: positionToRestore (chosen):', positionToRestore ? JSON.parse(JSON.stringify(positionToRestore)) : null);

  if (!positionToRestore) {
    if (displayMode.value === 'paginated') {
        // console.log('[CFS Pagi Debug] loadAndRestoreScroll (paginated): No positionToRestore found, calling scrollToTop().');
        scrollToTop();
    } else {
        // console.log('[CFS Pagi Debug] loadAndRestoreScroll (infinite): No positionToRestore found, will naturally be at top.');
    }
    // console.log('[CFS Pagi Debug] loadAndRestoreScroll: END (no position to restore)');
    return;
  }

  const targetScrollTop = positionToRestore.top;
  // console.log(`[CFS Pagi Debug] loadAndRestoreScroll: TargetScrollTop = ${targetScrollTop}, current mode = ${displayMode.value}`);

  if (displayMode.value === 'infinite') {
    isRestoringInfiniteScroll.value = true; 
    // console.log('[CFS Pagi Debug] loadAndRestoreScroll (infinite): Starting item setup to reach target page.');

    // Determine the target page for infinite scroll restoration
    let determinedTargetPage;
    const pageFromRouterState = (scrollTargetToRestore && typeof (scrollTargetToRestore as any).page === 'number') ? (scrollTargetToRestore as any).page : null; 
    const pageFromLocalStorage = (storedStateForPage && typeof storedStateForPage.page === 'number') ? storedStateForPage.page : null;

    if (pageFromLocalStorage && storedStateForPage?.scrollTop !== undefined && Math.abs(storedStateForPage.scrollTop - targetScrollTop) < estimatedItemHeight.value / 2) {
        determinedTargetPage = pageFromLocalStorage;
        // console.log(`[CFS Pagi Debug] Using pageFromLocalStorage: ${determinedTargetPage} (scrollTop was close: store ${storedStateForPage.scrollTop} vs target ${targetScrollTop})`);
    } else if (pageFromRouterState) {
        determinedTargetPage = pageFromRouterState;
        // console.log(`[CFS Pagi Debug] Using pageFromRouterState: ${determinedTargetPage}`);
    } else {
        determinedTargetPage = Math.ceil((targetScrollTop + 1) / (estimatedItemHeight.value * itemsPerPageForPagination)) || 1;
        // console.log(`[CFS Pagi Debug] Using calculatedTargetPage: ${determinedTargetPage}`);
    }
    
    const targetPageToLoad = determinedTargetPage;
    
    // console.log(`[CFS Pagi Debug] loadAndRestoreScroll (infinite): Final targetPageToLoad: ${targetPageToLoad}, current infiniteScroll page: ${currentPage.value}`);

    // Ensure the vehiclesStore has loaded its data.
    if (!vehiclesStore.isDataLoaded && vehiclesStore.vehicles.length === 0) {
      // console.log('[CFS Pagi Debug] loadAndRestoreScroll (infinite): vehiclesStore not loaded, attempting fetch...');
      await vehiclesStore.fetchVehicles(); 
      await nextTick(); 
      // console.log('[CFS Pagi Debug] loadAndRestoreScroll (infinite): vehiclesStore fetch complete. Filtered count:', vehiclesStore.filteredVehicles.length);
    }

    if (currentPage.value !== targetPageToLoad) {
      currentPage.value = targetPageToLoad;
      // console.log(`[CFS Pagi Debug] loadAndRestoreScroll (infinite): Set useInfiniteScroll's currentPage to ${targetPageToLoad}. Its 'items' should now recompute.`);
      await nextTick(); 
    }

  } 
  // For paginated mode, items for the current page are already sliced. No need to load more.

  await nextTick(); // Common nextTick for DOM updates from either mode
  if (displayMode.value === 'infinite') {
    isRestoringInfiniteScroll.value = false; 
    // console.log('[CFS Pagi Debug] loadAndRestoreScroll (infinite): Loading state false, DOM updated.');
  }
  
  const attemptScroll = (attempt = 1, maxAttempts = 5) => {
    if (displayMode.value === 'paginated') {
      // console.log(`[CFS Pagi Debug] attemptScroll (paginated): Attempt ${attempt}, target: ${targetScrollTop}, current: ${window.scrollY}`);
    }
    window.scrollTo({top: targetScrollTop, behavior: 'instant'});
    if (attempt < maxAttempts && Math.abs(window.scrollY - targetScrollTop) > 10) {
      if (displayMode.value === 'paginated') {
        // console.log(`[CFS Pagi Debug] attemptScroll (paginated): Discrepancy. Retrying in 100ms...`);
      }
      setTimeout(() => attemptScroll(attempt + 1), 100 * attempt);
    } else {
      if (displayMode.value === 'paginated') {
        // console.log(`[CFS Pagi Debug] attemptScroll (paginated): Scroll attempt ${attempt} finished. Final scrollY: ${window.scrollY}`);
      }
    }
  };
  requestAnimationFrame(() => { 
    requestAnimationFrame(() => { 
      attemptScroll();
    });
  });
  // console.log('[CFS Pagi Debug] loadAndRestoreScroll: END (scroll attempt initiated via rAF)');
};

// --- Lifecycle Hooks ---
onMounted(async () => {
  isMounted.value = true
  scrollContainer.value = document.documentElement // For standard window scrolling

  // Get saved layout and card type from localStorage if available
  if (process.client) {
    const savedLayout = localStorage.getItem('preferredViewMode')
    if (savedLayout && ['grid', 'list', 'advanced-list'].includes(savedLayout)) {
      cardLayout.value = savedLayout
      // If restoring to advanced-list, ensure displayMode is infinite
      if (savedLayout === 'advanced-list') {
        displayMode.value = 'infinite'
      }
    }
    
    const savedCardType = localStorage.getItem('preferredCardType')
    if (savedCardType && ['ModernVehicleCard', 'ClassicVehicleCard', 'RentalVehicleCard', 'CompactVehicleCard', 'PromoVehicleCard', 'MinimalVehicleCard'].includes(savedCardType)) {
      selectedCardType.value = savedCardType as any
    }
  }

  (vehiclesStore as any).initFavorites(); 
  cardLayout.value = process.client ? (localStorage.getItem('preferredViewMode') || 'grid') : 'grid'
  
  const preferredCardTypeFromStorage = process.client ? localStorage.getItem('preferredCardType') : null;
  const validCardTypes: Array<typeof selectedCardType.value> = ['ModernVehicleCard', 'ClassicVehicleCard', 'RentalVehicleCard', 'CompactVehicleCard', 'PromoVehicleCard', 'MinimalVehicleCard'];
  if (preferredCardTypeFromStorage && validCardTypes.includes(preferredCardTypeFromStorage as any)) {
    selectedCardType.value = preferredCardTypeFromStorage as typeof selectedCardType.value;
  } else {
    selectedCardType.value = 'ModernVehicleCard'; // Default value
  }

  const storedDisplayMode = process.client ? localStorage.getItem('preferredDisplayModeCFS') : 'infinite';
  if (storedDisplayMode === 'paginated' || storedDisplayMode === 'infinite') {
    displayMode.value = storedDisplayMode as 'infinite' | 'paginated';
  }
  // console.log('[CFS Pagi Debug] onMounted: displayMode determined:', displayMode.value);

  if (!vehiclesStore.isDataLoaded && vehiclesStore.vehicles.length === 0) {
      try {
        await vehiclesStore.fetchVehicles()
      } catch (error) {
        console.error('[CarsForSale] onMounted: Error during fetchVehicles:', error);
      }
  }

  // Enable real-time vehicle updates
  try {
    const { useVehicleRealtime } = await import('~/composables/useVehicleRealtime')
    const { subscribeToVehicleUpdates } = useVehicleRealtime()
    await subscribeToVehicleUpdates()
    console.log('[CarsForSale] Real-time vehicle updates enabled')
  } catch (error) {
    console.warn('[CarsForSale] Could not enable real-time updates:', error)
  }
  
  await nextTick(); 

  const pageFromUrl = route.query.page ? parseInt(route.query.page as string, 10) : 1;
  if (displayMode.value === 'paginated') {
    currentPageForPagination.value = !isNaN(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 1;
    if (String(route.query.page) !== String(currentPageForPagination.value)) {
        router.replace({ query: { ...route.query, page: String(currentPageForPagination.value) }});
    }
    // REMOVED: scrollToTop(); - Let loadAndRestoreScroll handle scroll restoration or default to top.
  }
  // For infinite scroll, its page is handled by useInfiniteScroll composable reacting to URL or its own state.

  // console.log('[CFS Pagi Debug] onMounted: BEFORE call to loadAndRestoreScroll()');
  try {
    await loadAndRestoreScroll(); 
  } catch (error) {
    console.error('[CarsForSale] onMounted: Error during loadAndRestoreScroll:', error);
  }
  // console.log('[CFS Pagi Debug] onMounted: AFTER call to loadAndRestoreScroll()');
  
  // Conditionally add scroll listener for localStorage-based scroll saving (primarily for infinite scroll)
  if (process.client && displayMode.value === 'infinite') {
    window.addEventListener('scroll', debouncedSaveScrollState, { passive: true });
  }
  
  initialFiltersApplied.value = true;
  initialSortApplied.value = true;
  isMounted.value = true; 
})

onBeforeUnmount(() => {
  if (process.client) {
    window.removeEventListener('scroll', debouncedSaveScrollState); // Always try to remove, it's safe
    
    // Cleanup real-time subscription
    try {
      import('~/composables/useVehicleRealtime').then(({ useVehicleRealtime }) => {
        const { unsubscribeFromVehicleUpdates } = useVehicleRealtime()
        unsubscribeFromVehicleUpdates()
        console.log('[CarsForSale] Real-time subscription cleaned up')
      })
    } catch (error) {
      console.warn('[CarsForSale] Could not cleanup real-time subscription:', error)
    }
  }
})

// Define filter handling functions that modify store state
const handleFilterChange = (filter: { name: string; value: any }) => {
  if (filter.value === null || filter.value === '' || (Array.isArray(filter.value) && filter.value.length === 0)) {
    handleFilterClear(filter.name)
  } else {
    vehiclesStore.applyFilters({ ...vehiclesStore.activeFilters, [filter.name]: filter.value })
  }
}

const handleFilterClear = (filterName: string) => {
  const newFilters = { ...vehiclesStore.activeFilters }
  delete newFilters[filterName]
  vehiclesStore.applyFilters(newFilters)
}

const handleClearAllFilters = () => {
  vehiclesStore.applyFilters({}) // Clear filters in store, watcher will update URL
  // Also reset local UI state if necessary
  activeCondition.value = 'all'
  searchKeywords.value = ''
  vehiclesStore.setSortOption('price-asc')
}

// Computed loading state from the store
const loading = computed(() => vehiclesStore.isLoading)

// Use a simple computed for displayed vehicles for now
const displayedVehicles = computed(() => vehiclesStore.filteredVehicles)

// Get vehicle IDs for quick view navigation
const filteredVehicleIds = computed(() => {
  return displayedVehicles.value.map((vehicle: Vehicle) => vehicle.id)
})

// Handle quick view close
const handleQuickViewClose = () => {
  closeQuickView() // Use composable
}

// Handle navigation between vehicles in quick view
const handleNavigate = (vehicleId: string) => {
  // This might be for prev/next within quick view
  // We only need to update the URL query parameter
  router.replace({ query: { ...route.query, quickView: vehicleId } })
}

// Watch route query for quickView changes (separate from filter sync)
watch(() => route.query.quickView,
  (newQuickView) => {
    const qvId = newQuickView as string | undefined
    if (qvId && qvId !== quickViewVehicleId.value) {
      openQuickView(qvId)
    } else if (!qvId && isQuickViewOpen.value) {
      closeQuickView()
    }
  },
  { immediate: true }
)

// Get the total count of filtered vehicles
const filteredVehiclesCount = computed(() => {
  return vehiclesStore.filteredVehicles?.length || 0
})

// Get the count of active filters
const actualActiveFilterCount = computed(() => {
  return vehiclesStore.activeFilters ? Object.keys(vehiclesStore.activeFilters).length : 0
})

// Mobile Filters Sheet state - This is likely no longer needed as VehicleFilters.vue handles its own mobile sheet
// const isFiltersVisible = ref(false) // Comment out or remove

// Simplified favorite function
const isFavorite = (id: string) => false // Placeholder

// Computed property to ensure undefined instead of null for the prop
const vehicleIdForQuickViewProp = computed(() => {
  return quickViewVehicleId.value === null ? undefined : quickViewVehicleId.value
})

// --- Helper Functions ---
const scrollToTop = () => {
  if (process.client) {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }
};

// --- State for Scroll-to-Top on Change Logic ---
const initialFiltersApplied = ref(false);
const initialSortApplied = ref(false);

// --- Watchers for Scrolling to Top on Change (Post-Initial Load) ---
// Watch the stringified version of activeFilters to correctly detect changes
watch(() => JSON.stringify(vehiclesStore.activeFilters), (newFiltersJson, oldFiltersJson) => {
  if (initialFiltersApplied.value && newFiltersJson !== oldFiltersJson) {
    // Filter changes now trigger a router.replace which includes page reset logic
    // and scrollToTop is called there if paginated.
    // For infinite scroll, useInfiniteScroll's resetOnDataChange handles it.
    // So, direct scrollToTop() here might be redundant or conflict.
    // nextTick(() => {
    //   scrollToTop();
    // });
  }
}); 

watch(() => vehiclesStore.sortOption, (newSort, oldSort) => {
  if (initialSortApplied.value && newSort !== oldSort) {
    // Sort changes also lead to page reset (handled by filter/URL watchers)
    // and potentially scrollToTop.
    // nextTick(() => {
    //   scrollToTop();
    // });
  }
});

// Watch route.query.sort to sync local select and store (already exists, ensure it's robust)
// This existing watcher seems to handle sort changes from URL correctly.
watch(() => route.query.sort, (newSortParam) => { // Renamed from newSort to newSortParam for clarity
  const currentUiSort = sortOptionForSelect.value;
  const currentStoreSort = vehiclesStore.sortOption;
  const defaultSortOption = 'relevance';

  let targetSort = defaultSortOption;
  if (newSortParam && typeof newSortParam === 'string') {
    const validSortOptions = ['relevance', 'price_asc', 'price_desc', 'year_desc', 'year_asc', 'updated_desc', 'kms_asc', 'kms_desc'];
    if (validSortOptions.includes(newSortParam)) {
        targetSort = newSortParam;
    }
  } else if (newSortParam === null || newSortParam === undefined) {
    targetSort = defaultSortOption;
  }

  if (currentUiSort !== targetSort) {
    sortOptionForSelect.value = targetSort;
  }

  if (currentStoreSort !== targetSort) {
    vehiclesStore.setSortOption(targetSort); // This will trigger store sortOption watcher if any, and data refiltering
  }
  // If sort option leads to actual data change, the scroll to top on data change should handle it.
  // Or if pagination mode, page will be reset to 1.
}, { immediate: true });

// Watch display mode for changes and save to localStorage
watch(displayMode, async (newMode, oldMode) => {
  if (process.client) {
    localStorage.setItem('preferredDisplayModeCFS', newMode);
  }

  const currentQuery = { ...route.query };
  
  if (newMode === 'paginated') {
    const pageFromUrl = currentQuery.page ? parseInt(currentQuery.page as string, 10) : 1;
    currentPageForPagination.value = !isNaN(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 1;
    
    // Ensure URL reflects pagination's current page, taking precedence
    if (String(currentQuery.page) !== String(currentPageForPagination.value)) {
      router.replace({ query: { ...currentQuery, page: String(currentPageForPagination.value) } });
    }
    scrollToTop();
    if (process.client) {
        window.removeEventListener('scroll', debouncedSaveScrollState);
    }
  } else { // Switching to 'infinite'
    // When switching to infinite, useInfiniteScroll will take over URL page management again.
    // Reset it to ensure it re-evaluates based on current URL or its own state.
    if (typeof resetInfiniteScroll === 'function') {
        resetInfiniteScroll(); 
    }
    // It should pick up the page from URL due to enableUrlSync:true and immediate:true (on reset)

    if (process.client) {
        window.addEventListener('scroll', debouncedSaveScrollState, { passive: true });
    }
    await nextTick();
    // Scroll restoration for infinite mode might be needed here if reset doesn't trigger it fully.
    // For now, rely on user scrolling or a subsequent loadAndRestoreScroll call if one happens.
    // Consider if loadAndRestoreScroll() should be explicitly called after reset and nextTick.
    // E.g., await loadAndRestoreScroll(); (if it's safe and displayMode check is inside it)
  }
});

watch(() => vehiclesStore.activeFilters, (newFilters) => {
  const filterQueryParts = getFilterQueryParts(newFilters || {});
  let finalQuery = { ...route.query, ...filterQueryParts };

  const preservedQuery: Record<string, any> = {};
  if (route.query.sort) preservedQuery.sort = route.query.sort;
  if (route.query.quickView) preservedQuery.quickView = route.query.quickView;
  
  finalQuery = { ...preservedQuery, ...filterQueryParts };

  if (displayMode.value === 'paginated') {
    finalQuery.page = '1';
    currentPageForPagination.value = 1; // Sync state
  } else {
    // For infinite scroll, its resetOnDataChange should handle resetting its page.
    // If its enableUrlSync is true, it will set page=1 (or its initial page) in URL.
    // Explicitly removing page from finalQuery lets infinite scroll control it.
    delete finalQuery.page;
  }
  
  // Only replace if query actually changes to avoid navigation loops
  if (JSON.stringify(router.currentRoute.value.query) !== JSON.stringify(finalQuery)) {
    router.replace({ query: finalQuery });
    // After filters change and URL is updated, scroll to top.
    nextTick(() => {
      scrollToTop(); 
      // Ensure infinite scroll also visually reflects its page 1 if it reset.
      // Its internal reset due to resetOnDataChange should handle its items.
    });
  }

}, { deep: true })

// Watch for sort option changes to reset page and scroll to top
watch(() => vehiclesStore.sortOption, (newSort, oldSort) => {
  if (newSort !== oldSort && isMounted.value) { // Ensure it's not an initial set and component is mounted
    // console.log(`[CFS Debug] Sort option changed from ${oldSort} to ${newSort}. Resetting page and scrolling top.`);
    if (displayMode.value === 'paginated') {
      if (currentPageForPagination.value !== 1) {
        currentPageForPagination.value = 1; 
      }
      // Update URL to reflect page 1 for pagination
      // Ensure other query params are preserved, only page is set to 1.
      const newQuery = { ...route.query, page: '1' };
      // Only replace if the query needs to change (e.g., page wasn't 1 or page param was missing)
      if (route.query.page !== '1') {
         router.replace({ query: newQuery });
      }
    } else { // Infinite mode
      // useInfiniteScroll should have reset its currentPage to 1 due to resetOnDataChange.
      // Its enableUrlSync will handle removing page from URL or setting to 1.
      // To be safe, if filters didn't change but sort did, explicitly ensure URL reflects page 1 state for infinite.
      // This relies on useInfiniteScroll updating URL to page=1 or removing page query.
      // If a page query param exists and it's not what useInfiniteScroll would set for its page 1, clear it.
      if (route.query.page) { 
        const queryWithoutPage = { ...route.query };
        delete queryWithoutPage.page;
        // Only replace if the query actually had a 'page' to remove.
        // This is a bit indirect; ideally useInfiniteScroll signals its URL state for page 1.
        router.replace({ query: queryWithoutPage }); 
      }
    }
    nextTick(() => {
      scrollToTop();
    });
  }
}, { flush: 'post' }); // flush: 'post' to ensure other reactive updates triggered by sort have settled.
</script>
<style>

.desktop-filters .scrollarea-body{
  padding-bottom: 20px;
}
.header-is-sticky .desktop-filters .scrollarea-body{
  padding-bottom: 0;
}
.desktop-filters.h-vh{
  height: 98vh!important;
}

</style>
